import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { Search, MapPin, MapPinned, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { getParcelRef, type ParcelRecord } from '@/utils/parcelGeometry';
import { haversineKm, formatDistance } from '@/utils/distance';
import {
  useAddressSearchIndex,
  searchAddressIndex,
  type AddressSearchMatch,
  type StreetAliasLike,
} from '@/hooks/useAddressSearchIndex';
import { loadTenantStreetAliases } from '@/utils/streetAliasCache';
import {
  useMemberSearchIndex,
  searchMembers,
  type MemberSearchEntry,
  type MemberSearchSource,
} from '@/hooks/useMemberSearchIndex';
import { searchOsmRoads, type OsmRoadMatch } from '@/hooks/useOsmRoadIndex';
import { loadTenantOsmRoads, type OsmRoadRecord } from '@/utils/osmRoadCache';
// Legacy JS api modules — typed via the permissive @/api/* shim.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as fpaMembersApi from '@/api/fpaMembers';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { listLinkedParcelRefs } from '@/api/parcels';

type SearchMode = 'location' | 'member';

interface MapSearchControlProps {
  /** Scopes the admin-curated alias list and the member index; omit to
   *  search official street names only (both are search enhancements). */
  tenantId?: number | null;
  /** The tenant's parcels, as currently rendered by ParcelOverlay. */
  parcels: ParcelRecord[];
  map: L.Map | null;
  /** Forwarded from the page's own MapCanvas prop so a selected result opens
   *  the same detail drawer a tapped parcel would. */
  onParcelSelect?: (parcel: ParcelRecord, latlng: L.LatLng) => void;
  className?: string;
}

const DEBOUNCE_MS = 120;
const HIGHLIGHT_MS = 4000;

/**
 * Map-chrome search icon — two modes:
 *   - **Location**: street/house-number search over the tenant's already-
 *     loaded parcels (unchanged from before), plus any members whose
 *     free-text address matches the query.
 *   - **Member**: name-first member search (falls back to address fields),
 *     gated on `fpa_member_list`/staff — never fetched or shown otherwise,
 *     matching the same privacy gate `MapSettings` applies to the parcels
 *     toggle (see `utils/member_privacy.py` on the backend).
 *
 * Every result row shows distance from the map's center when the popover
 * opened; a member result adds a type badge + "view member" icon; every
 * row keeps the "zoom to location" pin icon. Follows the same icon-button-
 * into-Popover/Sheet shape as `MapSettings`/`MapMaximize`.
 */
export function MapSearchControl({
  tenantId = null,
  parcels,
  map,
  onParcelSelect,
  className,
}: MapSearchControlProps) {
  const { isMobile } = useMobileLayout();
  const navigate = useNavigate();
  const { isAuthenticated, isStaff, can } = useAuth();
  // Same gate MapSettings uses for the parcels toggle — a role without
  // fpa_member_list must never see member names/addresses, so the fetch
  // below is skipped entirely rather than merely hiding the UI.
  const canSearchMembers = isAuthenticated && (isStaff() || can('fpa_member', 'list'));

  const [mode, setMode] = useState<SearchMode>('location');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [aliases, setAliases] = useState<StreetAliasLike[]>([]);
  const [rawMembers, setRawMembers] = useState<MemberSearchSource[]>([]);
  const [parcelRefByMemberId, setParcelRefByMemberId] = useState<Map<number, string>>(new Map());
  const [osmRoads, setOsmRoads] = useState<OsmRoadRecord[]>([]);
  const [origin, setOrigin] = useState<[number, number] | null>(null);

  const highlightMarkerRef = useRef<L.CircleMarker | L.Polyline | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (tenantId == null) {
      setAliases([]);
      return;
    }
    let cancelled = false;
    loadTenantStreetAliases(tenantId).then((list) => {
      if (!cancelled) setAliases(list);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Member data is fetched only when the viewer is allowed to see it —
  // never merely hidden behind UI. getMembersBulkCached carries its own
  // version-checked cache, so this is cheap after the first load.
  useEffect(() => {
    if (tenantId == null || !canSearchMembers) {
      setRawMembers([]);
      setParcelRefByMemberId(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [bulk, links] = await Promise.all([
          fpaMembersApi.getMembersBulkCached(tenantId),
          listLinkedParcelRefs(tenantId),
        ]);
        if (cancelled) return;
        setRawMembers(bulk?.members ?? []);
        const map2 = new Map<number, string>();
        for (const link of links?.links ?? []) {
          if (link?.member_id != null && link.parcel_ref) map2.set(link.member_id, link.parcel_ref);
        }
        setParcelRefByMemberId(map2);
      } catch (err) {
        console.warn('Failed to load members for search:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, canSearchMembers]);

  // OSM roads are public map data (no member/address info attached), so
  // unlike member search this has no permission gate — loaded whenever a
  // tenant is set.
  useEffect(() => {
    if (tenantId == null) {
      setOsmRoads([]);
      return;
    }
    let cancelled = false;
    loadTenantOsmRoads(tenantId).then((list) => {
      if (!cancelled) setOsmRoads(list);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setExpandedKey(null);
  }, [debouncedQuery]);

  // Reset on close so the next open starts fresh; capture the map's current
  // center as the "distance from" origin for this search session.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setExpandedKey(null);
      return;
    }
    if (map) {
      const c = map.getCenter();
      setOrigin([c.lat, c.lng]);
    }
  }, [open, map]);

  // Drop the highlight marker on unmount (popover close leaves it — the
  // user just picked this result and likely wants to keep seeing it).
  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) window.clearTimeout(highlightTimeoutRef.current);
      if (highlightMarkerRef.current && map) map.removeLayer(highlightMarkerRef.current);
    },
    [map],
  );

  const addressIndex = useAddressSearchIndex(parcels, aliases);
  const memberEntries = useMemberSearchIndex(rawMembers, parcelRefByMemberId, addressIndex.refIndex);
  const parcelRefToMember = useMemo(() => {
    const m = new Map<string, MemberSearchEntry>();
    for (const e of memberEntries) {
      const ref = e.parcel ? getParcelRef(e.parcel) : null;
      if (ref) m.set(ref, e);
    }
    return m;
  }, [memberEntries]);

  const locationMatches = useMemo(
    () => (mode === 'location' ? searchAddressIndex(addressIndex, debouncedQuery) : []),
    [mode, addressIndex, debouncedQuery],
  );
  // Members whose free-text address matches — location search "can include
  // a member's address details" even when it differs from the official
  // cadastral street name. addressOnly so typing a street doesn't surface
  // every unrelated member whose *name* happens to match those letters.
  const locationMemberMatches = useMemo(
    () =>
      mode === 'location' && canSearchMembers
        ? searchMembers(memberEntries, debouncedQuery, { addressOnly: true, limit: 8 })
        : [],
    [mode, canSearchMembers, memberEntries, debouncedQuery],
  );
  const memberMatches = useMemo(
    () => (mode === 'member' ? searchMembers(memberEntries, debouncedQuery) : []),
    [mode, memberEntries, debouncedQuery],
  );
  // OSM-sourced roads that aren't already covered by a cadastral street
  // name — fills gaps like a road with no adjoining named parcel, without
  // duplicating results the cadastral index already answers better (with
  // house numbers).
  const osmRoadMatches = useMemo(
    () =>
      mode === 'location'
        ? searchOsmRoads(osmRoads, debouncedQuery, new Set(addressIndex.byKey.keys()), 8)
        : [],
    [mode, osmRoads, debouncedQuery, addressIndex],
  );

  const distanceTo = (parcel: ParcelRecord | null | undefined): number | null => {
    if (!origin || !parcel || parcel.centroid_lat == null || parcel.centroid_lon == null) return null;
    return haversineKm(origin, [parcel.centroid_lat, parcel.centroid_lon]);
  };
  const distanceToPoint = (point: [number, number] | null | undefined): number | null => {
    if (!origin || !point) return null;
    return haversineKm(origin, point);
  };
  const closestDistance = (entries: Array<{ parcel: ParcelRecord }>): number | null => {
    let min: number | null = null;
    for (const e of entries) {
      const d = distanceTo(e.parcel);
      if (d != null && (min == null || d < min)) min = d;
    }
    return min;
  };

  const clearHighlight = () => {
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    if (highlightMarkerRef.current && map) {
      map.removeLayer(highlightMarkerRef.current);
      highlightMarkerRef.current = null;
    }
  };

  // Imperative Leaflet layer rather than a declarative <CircleMarker> — this
  // component renders in MapCanvas's chrome div, a sibling of <MapContainer>,
  // not inside it, so react-leaflet's declarative layers aren't available
  // here. `map` itself doesn't care where the imperative call comes from.
  const highlightPoint = (lat: number, lon: number) => {
    if (!map) return;
    clearHighlight();
    const marker = L.circleMarker([lat, lon], {
      radius: 14,
      color: '#7b1fa2',
      weight: 3,
      fillColor: '#ce93d8',
      fillOpacity: 0.35,
    }).addTo(map);
    highlightMarkerRef.current = marker;
    highlightTimeoutRef.current = window.setTimeout(() => {
      if (highlightMarkerRef.current === marker) clearHighlight();
    }, HIGHLIGHT_MS);
  };

  // Same idea as highlightPoint, but for a road's line geometry instead of
  // a single parcel point — a purple outline along the actual road rather
  // than a dot at one end of it.
  const highlightLines = (segments: Array<Array<[number, number]>>) => {
    if (!map || segments.length === 0) return;
    clearHighlight();
    const line = L.polyline(segments, { color: '#7b1fa2', weight: 5, opacity: 0.7 }).addTo(map);
    highlightMarkerRef.current = line;
    highlightTimeoutRef.current = window.setTimeout(() => {
      if (highlightMarkerRef.current === line) clearHighlight();
    }, HIGHLIGHT_MS);
  };

  const flyToOsmRoad = (match: OsmRoadMatch) => {
    if (!map) return;
    const { road } = match;
    const points = road.segments.flat();
    if (points.length === 0) {
      if (!road.point) return;
      map.flyTo(road.point, 16);
    } else if (points.length === 1) {
      map.flyTo(points[0]!, 16);
    } else {
      map.fitBounds(L.latLngBounds(points), { maxZoom: 17, padding: [40, 40] });
    }
    highlightLines(road.segments);
    setOpen(false);
  };

  const flyToParcel = (parcel: ParcelRecord) => {
    if (!map) return;
    const lat = parcel.centroid_lat;
    const lon = parcel.centroid_lon;
    if (lat == null || lon == null) return;
    map.flyTo([lat, lon], 18);
    highlightPoint(lat, lon);
    onParcelSelect?.(parcel, L.latLng(lat, lon));
    setOpen(false);
  };

  const handleResultClick = (match: AddressSearchMatch) => {
    if (!map) return;
    const { group, houseNoFilter } = match;
    let entries = group.entries;
    if (houseNoFilter) {
      const norm = houseNoFilter.toLowerCase();
      const filtered = entries.filter((e) => e.houseNo?.toLowerCase() === norm);
      if (filtered.length > 0) entries = filtered;
    }
    if (entries.length === 1) {
      flyToParcel(entries[0]!.parcel);
      return;
    }
    const withCentroid = entries.filter(
      (e) => e.parcel.centroid_lat != null && e.parcel.centroid_lon != null,
    );
    if (withCentroid.length > 0) {
      const bounds = L.latLngBounds(
        withCentroid.map(
          (e) => [e.parcel.centroid_lat as number, e.parcel.centroid_lon as number] as [number, number],
        ),
      );
      map.fitBounds(bounds, { maxZoom: 17, padding: [40, 40] });
    }
    // Multiple results, no single-entry resolution — keep the popover open
    // and expand the house-number list so the user can narrow it down.
    setExpandedKey((k) => (k === group.key ? null : group.key));
  };

  const handleViewMember = (entry: MemberSearchEntry) => {
    // Same zoom-and-highlight the pin-drop button does, when there's a
    // linked parcel to zoom to — then go to the member's own page.
    if (entry.parcel) flyToParcel(entry.parcel);
    else setOpen(false);
    navigate(`/members/fpa/${entry.id}`);
  };

  const handleMemberPinDrop = (entry: MemberSearchEntry) => {
    if (entry.parcel) flyToParcel(entry.parcel);
  };

  const body = (
    <>
      {canSearchMembers && (
        <div className="flex gap-1 border-b border-border p-2">
          <Button
            type="button"
            size="sm"
            variant={mode === 'location' ? 'secondary' : 'ghost'}
            className="flex-1 gap-1.5"
            onClick={() => setMode('location')}
          >
            <MapPin className="h-3.5 w-3.5" />
            Location
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'member' ? 'secondary' : 'ghost'}
            className="flex-1 gap-1.5"
            onClick={() => setMode('member')}
          >
            <User className="h-3.5 w-3.5" />
            Member
          </Button>
        </div>
      )}
      <div className="border-b border-border p-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            mode === 'member' ? 'Search member name…' : 'Search street or house number…'
          }
          autoFocus
        />
      </div>
      <div className="max-h-72 overflow-y-auto">
        {debouncedQuery.trim() === '' ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {mode === 'member'
              ? 'Start typing a member name (or address).'
              : 'Start typing a street name or house number.'}
          </div>
        ) : mode === 'member' ? (
          memberMatches.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No members match.
            </div>
          ) : (
            <ul>
              {memberMatches.map((m) => (
                <MemberRow
                  key={m.entry.id}
                  entry={m.entry}
                  distanceKm={distanceTo(m.entry.parcel)}
                  onView={() => handleViewMember(m.entry)}
                  onPinDrop={() => handleMemberPinDrop(m.entry)}
                />
              ))}
            </ul>
          )
        ) : locationMatches.length === 0 &&
          locationMemberMatches.length === 0 &&
          osmRoadMatches.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            No matches within this FPA.
          </div>
        ) : (
          <>
            {locationMatches.length > 0 && (
              <ul>
                {locationMatches.map((match) => (
                  <ResultRow
                    key={match.group.key}
                    match={match}
                    expanded={expandedKey === match.group.key}
                    distanceKm={closestDistance(match.group.entries)}
                    onClick={() => handleResultClick(match)}
                    onSelectEntry={flyToParcel}
                    memberByRef={parcelRefToMember}
                    distanceToParcel={distanceTo}
                    onViewMember={handleViewMember}
                  />
                ))}
              </ul>
            )}
            {locationMemberMatches.length > 0 && (
              <>
                <div className="border-t border-border bg-muted/40 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Members at this address
                </div>
                <ul>
                  {locationMemberMatches.map((m) => (
                    <MemberRow
                      key={`addr-${m.entry.id}`}
                      entry={m.entry}
                      distanceKm={distanceTo(m.entry.parcel)}
                      onView={() => handleViewMember(m.entry)}
                      onPinDrop={() => handleMemberPinDrop(m.entry)}
                    />
                  ))}
                </ul>
              </>
            )}
            {osmRoadMatches.length > 0 && (
              <>
                <div className="border-t border-border bg-muted/40 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Other roads
                </div>
                <ul>
                  {osmRoadMatches.map((m) => (
                    <OsmRoadRow
                      key={m.road.id}
                      match={m}
                      distanceKm={distanceToPoint(m.road.point)}
                      onClick={() => flyToOsmRoad(m)}
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </>
  );

  const trigger = (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      className={cn('shadow-md', className)}
      aria-label="Search the map"
    >
      <Search className="h-4 w-4" />
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto p-0">
          <SheetHeader className="px-4 pt-4">
            <SheetTitle>Search the map</SheetTitle>
          </SheetHeader>
          <div className="mt-2">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {body}
      </PopoverContent>
    </Popover>
  );
}

function ResultRow({
  match,
  expanded,
  distanceKm,
  onClick,
  onSelectEntry,
  memberByRef,
  distanceToParcel,
  onViewMember,
}: {
  match: AddressSearchMatch;
  expanded: boolean;
  distanceKm: number | null;
  onClick: () => void;
  onSelectEntry: (parcel: ParcelRecord) => void;
  memberByRef: Map<string, MemberSearchEntry>;
  distanceToParcel: (parcel: ParcelRecord | null | undefined) => number | null;
  onViewMember: (entry: MemberSearchEntry) => void;
}) {
  const { group } = match;
  const count = group.entries.length;
  const sortedEntries = useMemo(
    () =>
      group.entries
        .slice()
        .sort((a, b) => (a.houseNo || '').localeCompare(b.houseNo || '', undefined, { numeric: true })),
    [group],
  );

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
      >
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">
          {group.displayName}
          {group.suburb && <span className="text-muted-foreground"> · {group.suburb}</span>}
        </span>
        {distanceKm != null && (
          <span className="shrink-0 text-xs text-muted-foreground">{formatDistance(distanceKm)}</span>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">
          {count} {count === 1 ? 'result' : 'results'}
        </span>
        <MapPinned className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
      {expanded && count > 1 && (
        <ul className="border-t border-border bg-muted/30">
          {sortedEntries.map((entry, i) => {
            const ref = getParcelRef(entry.parcel);
            const member = ref ? memberByRef.get(ref) : undefined;
            const d = distanceToParcel(entry.parcel);
            return (
              <li
                key={`${entry.houseNo ?? 'no-no'}-${i}`}
                className="flex items-center gap-1 py-1.5 pl-9 pr-2 text-xs hover:bg-muted"
              >
                <button
                  type="button"
                  onClick={() => onSelectEntry(entry.parcel)}
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  <span>{entry.houseNo || 'No house number'}</span>
                  {member && (
                    <Badge
                      variant={member.memberType === 'non_member' ? 'secondary' : 'outline'}
                      className="h-4 px-1 text-[10px] font-normal"
                    >
                      {member.memberType === 'non_member' ? 'Non-member' : 'Member'}
                    </Badge>
                  )}
                  {d != null && <span className="text-muted-foreground">{formatDistance(d)}</span>}
                </button>
                {member && (
                  <button
                    type="button"
                    title="View member"
                    onClick={() => onViewMember(member)}
                    className="shrink-0 rounded p-1 hover:bg-background"
                  >
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                )}
                <button
                  type="button"
                  title="Zoom to location"
                  onClick={() => onSelectEntry(entry.parcel)}
                  className="shrink-0 rounded p-1 hover:bg-background"
                >
                  <MapPinned className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function MemberRow({
  entry,
  distanceKm,
  onView,
  onPinDrop,
}: {
  entry: MemberSearchEntry;
  distanceKm: number | null;
  onView: () => void;
  onPinDrop: () => void;
}) {
  const hasLocation = Boolean(entry.parcel);
  const address = [entry.plotNumber, entry.street, entry.areaSuburb].filter(Boolean).join(', ');

  return (
    <li className="flex items-center gap-1 px-3 py-2 text-sm hover:bg-muted">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-medium">{entry.name}</span>
          <Badge
            variant={entry.memberType === 'non_member' ? 'secondary' : 'outline'}
            className="h-4 shrink-0 px-1 text-[10px] font-normal"
          >
            {entry.memberType === 'non_member' ? 'Non-member' : 'Member'}
          </Badge>
        </div>
        {address && <div className="truncate text-xs text-muted-foreground">{address}</div>}
      </div>
      {distanceKm != null && (
        <span className="shrink-0 text-xs text-muted-foreground">{formatDistance(distanceKm)}</span>
      )}
      <button
        type="button"
        title="View member"
        onClick={onView}
        className="shrink-0 rounded p-1.5 hover:bg-background"
      >
        <User className="h-4 w-4 text-muted-foreground" />
      </button>
      <button
        type="button"
        title={hasLocation ? 'Zoom to location' : 'No linked parcel'}
        disabled={!hasLocation}
        onClick={onPinDrop}
        className="shrink-0 rounded p-1.5 hover:bg-background disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <MapPinned className="h-4 w-4 text-muted-foreground" />
      </button>
    </li>
  );
}

function OsmRoadRow({
  match,
  distanceKm,
  onClick,
}: {
  match: OsmRoadMatch;
  distanceKm: number | null;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
      >
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{match.road.name}</span>
        {distanceKm != null && (
          <span className="shrink-0 text-xs text-muted-foreground">{formatDistance(distanceKm)}</span>
        )}
        <MapPinned className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
    </li>
  );
}

export default MapSearchControl;
